import React, {useState, useEffect, useContext} from 'react';
import {Form, Input, Select, Checkbox, Row, Col, Divider} from 'antd';
import {UserContext} from "@context/UserContext";
import Button from "@common/Button";

const AccountSettingsUserDetails = props => {
    const {userDetails, data, checkFormStatus, handleSubmitForm} = props;
    const [regions, setRegions] = useState([]);
    const [initialFormValues, setInitialFormValues] = useState({});
    const [submittable, setSubmittable] = useState(false);
    const [disabled, setDisabled] = useState(false);
    const {user} = useContext(UserContext);
    const [form] = Form.useForm();

    useEffect(() => {
        if (userDetails) {
            const newInitialValues = {
                title: userDetails.title || null,
                firstName: userDetails.firstName || null,
                lastName: userDetails.lastName || null,
                username: userDetails.username || null,
                email: userDetails.email || null,
                gender: userDetails.gender || null,
                countryId: userDetails.countryId || null,
                regionId: userDetails.regionId || null,
                postcode: userDetails.postcode || null,
                anonymous: userDetails.anonymous || false,
                cambridgeOptIn: userDetails.cambridgeOptIn || false,
            };
            form.setFieldsValue(newInitialValues);
            setInitialFormValues(newInitialValues);
            setRegions(data?.countries.find(country => country.countryCode === userDetails?.countryCode)?.regions || []);
        }
    }, [userDetails, data, form]);

    const handleCountryChange = (countryId) => {
        const selectedCountry = data?.countries.find(country => country.countryId === countryId);
        if (selectedCountry?.regions?.length) {
            setRegions(selectedCountry.regions);
            if (userDetails.countryId === countryId) {
                form.setFieldsValue({regionId: userDetails.regionId || null});
            } else {
                form.setFieldsValue({regionId: null});
            }
        } else {
            setRegions([]);
            form.setFieldsValue({regionId: null});
        }
    };

    const handleSubmitFormWrapper = (values) => {
        setDisabled(true);
        handleSubmitForm(form, values, initialFormValues, '/web/ui/accountSettings/updateUserDetails', () => {
            displayToastMessage('Update successful', '', 'success', undefined, 4000);
            setInitialFormValues(values);
            setDisabled(false);
        }, () => {
            setDisabled(false);
        });
    }

    if (!user || !userDetails) {
        return <div>Loading...</div>;
    }

    return (
        <div>
            <h3>Your details</h3>
            <p>Update your details and manage account settings.</p>
            {userDetails.subscriberType === 'STUDENT' && (
                <p>Talk to your teacher to if you need to change your name.</p>
            )}
            <Divider />
            <Form
                name="yourDetails"
                form={form}
                layout="vertical"
                onFieldsChange={() => setSubmittable(checkFormStatus(form))}
                onFinish={handleSubmitFormWrapper}
                className={'ch-account-settings-form'}
            >
                {userDetails.subscriberType !== 'STUDENT' && (
                    <Form.Item
                        label="Title"
                        name="title"
                    >
                        <Select
                            allowClear
                            autoComplete="honorific-prefix"
                        >
                            {data?.titles.map((titleObj) => {
                                const [value, label] = Object.entries(titleObj)[0];
                                return (
                                    <Select.Option key={value} value={label}>
                                        {label}
                                    </Select.Option>
                                );
                            })}
                        </Select>
                    </Form.Item>
                )}
                <Form.Item
                    label="First Name"
                    name="firstName"
                    rules={data?.validation.firstName}
                    validateTrigger="onBlur"
                >
                    <Input
                        disabled={userDetails.subscriberType === 'STUDENT' || !user.allowEditUser}
                        autoComplete="given-name"
                        aria-required="true"
                    />
                </Form.Item>
                <Form.Item
                    label="Last Name"
                    name="lastName"
                    rules={data?.validation.lastName}
                    validateTrigger="onBlur"
                >
                    <Input
                        disabled={userDetails.subscriberType === 'STUDENT' || !user.allowEditUser}
                        autoComplete="family-name"
                        aria-required="true"
                    />
                </Form.Item>
                <Form.Item
                    label="Username"
                    name="username"
                    rules={data?.validation.username}
                    validateTrigger="onBlur"
                >
                    <Input
                        disabled={userDetails.preventChangeUsername || !user.allowEditUser}
                        autoComplete="username"
                        aria-required="true"
                    />
                </Form.Item>
                <Form.Item
                    label="Email"
                    name="email"
                    rules={data?.validation.email}
                    validateTrigger="onBlur"
                >
                    <Input
                        disabled={userDetails.preventChangeEmail || !user.allowEditUser}
                        type="email"
                        autoComplete="email"
                        aria-required="true"
                    />
                </Form.Item>
                <Form.Item
                    label="Gender"
                    name="gender"
                >
                    <Select
                        autoComplete="sex"
                    >
                        {data?.genders.map((genderObj) => {
                            const [value, label] = Object.entries(genderObj)[0];
                            return (
                                <Select.Option key={value} value={value}>
                                    {label}
                                </Select.Option>
                            );
                        })}
                    </Select>
                </Form.Item>
                {user.allowEditUser && (
                    <>
                        <Form.Item
                            label="Country"
                            name="countryId"
                        >
                            <Select
                                autoComplete="country-name"
                                onChange={handleCountryChange}
                            >
                                {data?.countries.map(country => {
                                    return (
                                        <Select.Option key={country.countryCode} value={country.countryId}>
                                            {country.countryName}
                                        </Select.Option>
                                    );
                                })}
                            </Select>
                        </Form.Item>
                        {regions.length > 0 && (
                            <Form.Item
                                label="Region"
                                name="regionId"
                            >
                                <Select
                                    autoComplete="address-level1"
                                >
                                    {regions.map(region => {
                                        return (
                                            <Select.Option key={region.regionCode} value={region.regionId}>
                                                {region.regionName}
                                            </Select.Option>
                                        );
                                    })}
                                </Select>
                            </Form.Item>
                        )}
                        <Form.Item
                            label="Postcode"
                            name="postcode"
                        >
                            <Input
                                maxLength={16}
                                autoComplete="postal-code"
                            />
                        </Form.Item>
                    </>
                )}
                <Form.Item name="anonymous" valuePropName="checked" className={'ch-account-settings-checkbox'}>
                    <Checkbox>Hide name on Scorcher leaderboard</Checkbox>
                </Form.Item>
                <Form.Item name="cambridgeOptIn" valuePropName="checked" className={'ch-account-settings-checkbox'}>
                    <Checkbox> I'd like to receive customer care emails offering advice and useful tips on using my Cambridge digital resource</Checkbox>
                </Form.Item>
                <p className={'ch-account-settings-disclaimer'}>HOTmaths Pty Ltd, trading as Cambridge HOTmaths, and its affiliate Cambridge University Press, may occasionally send you product information.</p>
                <Row>
                    <Col>
                        <Button type="primary" htmlType="submit" disabled={disabled ? disabled : !submittable}>
                            Save Changes
                        </Button>
                    </Col>
                </Row>
            </Form>
        </div>
    );
};

export default AccountSettingsUserDetails;